import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FileExtensionPreview, getFilename } from "@air/component-file-extension-preview";
import { Comment as CommentIcon, Ellipsis as EllipsisHorizontalIcon, Stack as StackIcon } from "@air/next-icons";
import { memo, useMemo } from "react";
import { CardAction } from "~/components/CardAction";
import { AssetGalleryCardMetaDetails } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardMetaDetails";
import { CustomFieldsDisplay } from "~/components/Gallery/CustomFieldsDisplay";
import { FileGalleryCardMenu } from "~/components/Gallery/FileGalleryCard/FileGalleryCardMenu";
import { GalleryCard } from "~/components/Gallery/GalleryCard/GalleryCard";
import { GalleryCardCustomFieldsAction } from "~/components/Gallery/GalleryCardCustomFieldsAction";
import { GalleryItemType } from "~/components/Gallery/types";
import { getAssetCustomFields } from "~/components/Gallery/utils";
import { FILE_CARD, FILE_CARD_SUB_MENU_BUTTON, FILE_CARD_TITLE } from "~/constants/testIDs";
import { visibleCustomFieldsViewOptionsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
const _FileGalleryCard = (param)=>{
    let { file, getItemMenuOptions, getSelectionMenuOptions, isSelectable = true, onCustomFieldsClick, onDiscussionsClick, onVersionsClick, onFileClick, onFileTitleCmdClick, canViewCustomFields, allCustomFields = [], cardWidth, getButtonClassName } = param;
    const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
    const customFields = useMemo(()=>getAssetCustomFields({
            allCustomFields,
            clip: file,
            visibleCustomFields
        }), [
        allCustomFields,
        file,
        visibleCustomFields
    ]);
    const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;
    const titleLabel = getFilename(file);
    const popoverCustomFields = useMemo(()=>{
        var _file_customFields;
        return file === null || file === void 0 ? void 0 : (_file_customFields = file.customFields) === null || _file_customFields === void 0 ? void 0 : _file_customFields.filter((customField)=>{
            return allCustomFields.some((cf)=>cf.id === customField.id);
        });
    }, [
        file,
        allCustomFields
    ]);
    return /*#__PURE__*/ _jsx(GalleryCard, {
        testId: FILE_CARD,
        title: titleLabel,
        getItemMenuOptions: getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        item: {
            id: file.id,
            item: file,
            type: GalleryItemType.file
        },
        onClick: onFileClick,
        isSelectable: isSelectable,
        getButtonClassName: getButtonClassName,
        children: (param)=>{
            let { setIsMenuOpen, isHovering } = param;
            return /*#__PURE__*/ _jsxs("div", {
                className: "pointer-events-none relative flex size-full flex-col content-between rounded-4 border border-grey-4 bg-grey-1 p-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none flex items-center",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-1 items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex size-8 shrink-0 items-center justify-center rounded-4 bg-pigeon-100",
                                        children: /*#__PURE__*/ _jsx(FileExtensionPreview, {
                                            ext: file.ext,
                                            color: "gray"
                                        })
                                    }),
                                    canViewCustomFields && !!(popoverCustomFields === null || popoverCustomFields === void 0 ? void 0 : popoverCustomFields.length) && /*#__PURE__*/ _jsx(GalleryCardCustomFieldsAction, {
                                        customFields: popoverCustomFields,
                                        onClick: onCustomFieldsClick
                                    }),
                                    !!onVersionsClick && /*#__PURE__*/ _jsx(CardAction, {
                                        "data-testid": "CARD_VERSIONS_INDICATOR",
                                        color: "light",
                                        icon: /*#__PURE__*/ _jsx(StackIcon, {
                                            className: "size-4"
                                        }),
                                        onClick: onVersionsClick
                                    }),
                                    !!onDiscussionsClick && !!file.openCommentCount && /*#__PURE__*/ _jsx(CardAction, {
                                        "data-testid": "CARD_DISCUSSIONS_INDICATOR",
                                        color: "light",
                                        icon: /*#__PURE__*/ _jsx(CommentIcon, {
                                            className: "size-4"
                                        }),
                                        onClick: onDiscussionsClick,
                                        text: file.openCommentCount
                                    })
                                ]
                            }),
                            isHovering && /*#__PURE__*/ _jsx("div", {
                                className: "flex shrink-0 gap-1",
                                children: /*#__PURE__*/ _jsx(FileGalleryCardMenu, {
                                    clip: file,
                                    getItemMenuOptions: getItemMenuOptions,
                                    getSelectionMenuOptions: getSelectionMenuOptions,
                                    setIsMenuOpen: setIsMenuOpen,
                                    trigger: /*#__PURE__*/ _jsx(CardAction, {
                                        color: "light",
                                        "data-testid": FILE_CARD_SUB_MENU_BUTTON,
                                        icon: /*#__PURE__*/ _jsx(EllipsisHorizontalIcon, {
                                            className: "size-4"
                                        })
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-3 flex grow flex-col justify-end gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(AssetGalleryCardMetaDetails, {
                                clip: file,
                                title: titleLabel,
                                onTitleClick: onFileClick,
                                onTitleCmdClick: onFileTitleCmdClick,
                                /** Card width minus the left and right padding */ width: cardWidth - 24,
                                titleTestId: FILE_CARD_TITLE
                            }),
                            shouldShowCustomFields && /*#__PURE__*/ _jsx(CustomFieldsDisplay, {
                                className: "px-1",
                                customFields: customFields,
                                onClick: onCustomFieldsClick
                            })
                        ]
                    })
                ]
            });
        }
    });
};
export const FileGalleryCard = /*#__PURE__*/ memo(_FileGalleryCard);
